/**
 * Outcome of the root's auto-select of an active organization.
 *
 *  - `idle`     — no attempt has been made yet (or the session already had an active organization).
 *  - `inFlight` — a `setActive` call is running.
 *  - `failed`   — Clerk rejected the switch, or the attempt outlived its deadline.
 *
 * Readers currently treat `idle` and `failed` alike: both are terminal, and both render the same
 * organization-picker card. The distinction is kept because the two have different causes and a
 * recovery surface that says "we could not switch you, retry" reads differently from "pick an
 * organization" — but until something renders that difference, this type promises more than the
 * system delivers.
 */
export type ActiveOrganizationAttempt = 'idle' | 'inFlight' | 'failed';

/**
 * How long an in-flight auto-select may run before readers stop waiting on it and offer the user
 * a control instead. Generous enough to cover a slow organization-health read plus the Clerk
 * round trip, short enough that nobody watches a placeholder wondering whether it is broken.
 */
export const ACTIVE_ORGANIZATION_DEADLINE_MS = 15000;

let attempt: ActiveOrganizationAttempt = 'idle';
let attemptUserId: string | null = null;
let deadlineTimer: ReturnType<typeof setTimeout> | undefined;
const listeners = new Set<() => void>();

function emit(): void {
  for (const listener of listeners) listener();
}

/**
 * Records where the root's auto-select stands.
 *
 * Context would also work — the reader is a descendant of the writer through the router's
 * `<Outlet/>`. This is an external store instead so that a transition re-renders only the
 * components that subscribe, rather than the whole routed subtree below the provider; the value
 * changes during the most render-sensitive moment of the page's life, when it is deciding
 * whether to keep waiting. `useSyncExternalStore` keeps that subscription correct under
 * concurrent rendering.
 *
 * The cost is that the state is process-wide rather than provider-scoped, so it must be reset
 * between tests. A user switch inside one page load is handled by scoping: reads are bound to a
 * user by `scopeActiveOrganizationAttempt`, and writes carry the user they were made for.
 */
export function setActiveOrganizationAttempt(
  next: ActiveOrganizationAttempt,
  userId: string | null
): void {
  // A write carries the user it was made for. An auto-select promise resolving after the session
  // has moved on would otherwise record its outcome against the new user and cancel that user's
  // own deadline, showing them the result of an attempt their session never made.
  //
  // Required rather than optional: an untagged write is exactly the bug, so there is no safe
  // default to fall back to. A caller with genuinely no user passes null and matches only the
  // unscoped store.
  if (userId !== attemptUserId) return;
  if (attempt === next) return;
  attempt = next;

  // A timer held by the effect that set `inFlight` is cancelled by React's cleanup when that
  // effect's dependencies change or its route unmounts, and the guard on re-entry then sees no
  // work to do — so the deadline lives here, where nothing can outrun it. Readers cannot tell a
  // claim nobody will resolve apart from a request still in progress.
  clearTimeout(deadlineTimer);
  if (next === 'inFlight') {
    // Routed back through this function so one place decides whether a write lands; a direct
    // mutation here would be a second, unguarded write path.
    deadlineTimer = setTimeout(() => {
      setActiveOrganizationAttempt('failed', attemptUserId);
    }, ACTIVE_ORGANIZATION_DEADLINE_MS);
  }

  emit();
}

export function getActiveOrganizationAttempt(): ActiveOrganizationAttempt {
  return attempt;
}

/**
 * Binds the recorded attempt to the user it was made for.
 *
 * The store outlives a client-side sign-out and sign-in, which in a single-page app happens with
 * no page load to clear it. Without this, a `failed` recorded for one user would still read as
 * `failed` for the next one, showing them a recovery control for an attempt their session never
 * made. Paired with the `userId` argument on writes, so a value can only ever describe the
 * session asking for it.
 */
export function scopeActiveOrganizationAttempt(userId: string | null): void {
  if (attemptUserId === userId) return;
  attemptUserId = userId;
  attempt = 'idle';
  clearTimeout(deadlineTimer);
  emit();
}

export function subscribeToActiveOrganizationAttempt(
  listener: () => void
): () => void {
  listeners.add(listener);
  return () => listeners.delete(listener);
}

/**
 * Test seam: clears the stored value, its user scope, and any pending deadline between cases, so
 * one test cannot leak into the next. Subscriptions are left intact.
 */
export function resetActiveOrganizationAttempt(): void {
  attempt = 'idle';
  attemptUserId = null;
  clearTimeout(deadlineTimer);
  deadlineTimer = undefined;
}
